import { FC, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import EmailIcon from '@mui/icons-material/Email';
import { Customer, UpdateEmail } from '@models';
import { setSelectedCustomer } from '@store/slices/customer';
import { useUpdateEmailMutation } from '@services';
import { useAppDispatch } from '@hooks/redux-custom';
import { showToast } from '@store/slices/notification';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  customer?: Customer;
};

const CustomerEmailChangeModal: FC<Props> = ({ customer, isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState<string>('');
  const [updateEmail, { isLoading }] = useUpdateEmailMutation();

  const changeEmail = async () => {
    const updateEmailPayload: UpdateEmail = {
      newemail: email,
      customerId: Number(customer?.id),
      oldemail: customer?.email,
    };

    updateEmail(updateEmailPayload)
      .unwrap()
      .then((data) => {
        console.log(data);

        if (data.code !== 200) {
          dispatch(
            showToast({
              message: data.message ?? 'Failed to update email!',
              type: 'error',
            }),
          );
        } else {
          dispatch(
            showToast({
              message: `Successfully changed ${customer?.first_name}'s email!`,
              type: 'success',
            }),
          );

          customer && dispatch(setSelectedCustomer({ ...customer, email: email }));
          onClose();
        }
      });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      data-testid="customer-email-change-modal"
      PaperProps={{
        style: { borderRadius: 10 },
      }}
    >
      <DialogContent>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <DialogTitle sx={{ fontSize: 30, m: 0, p: 2 }}>
            Change Email
            {onClose && (
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <Grid item xs={12}>
            <TextField id="email" label="Email" variant="outlined" defaultValue={customer?.email} fullWidth disabled />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus
              margin="dense"
              id="new-email"
              type="email"
              label="New Email"
              variant="outlined"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  display: 'block',
                  marginRight: '10px',
                  backgroundColor: '#aaaaaa',
                  '&.MuiButtonBase-root:hover': {
                    bgcolor: '#aaaaaa',
                  },
                }}
              >
                Cancel
              </Button>
              <LoadingButton variant="contained" loading={isLoading} onClick={changeEmail} data-testid="customer-email-update-btn">
                Update
              </LoadingButton>
            </DialogActions>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerEmailChangeModal;
